import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import ROUTES from '../../../shared/constants/routes';
import { DEEPLINK_PARAMS } from '../../../shared/constants/deeplinkParams';
import { isOnboardingTargetId } from './onboardingTargets';

/**
 * Puente de las URLs de aliado del onboarding v2 —muertas desde que el motor v3
 * reemplazó el funnel— hacia la maquinaria de targets que v3 sí entiende.
 *
 * Existe porque Zubale reparte `/onboarding-v2/ally/benefit/zubale/` en sus
 * comunicaciones: no podemos cambiar lo que ya está impreso y enviado, así que
 * esa URL tiene que seguir viva. Hoy cae en el 404 de la app.
 *
 * Traduce a `/login?onboarding_target=ally:<id>`, que es la puerta ÚNICA del
 * mecanismo existente: login estampa el prestamp del target (goal =
 * ally_recommended + ally_selected = zubale) y el walk del host sienta al
 * usuario en el flujo del aliado en lugar de goals. Sirve para los dos casos
 * sin ramificar: quien llega sin cuenta (el target viaja en sessionStorage a
 * través del OTP y lo consume usePostAuthRedirect) y quien ya trae sesión
 * (login lo estampa y entra directo).
 *
 * El id del aliado se busca en CUALQUIER segmento de la ruta porque v2 tenía
 * dos formas en circulación: `/ally/benefit/<id>` y `/ally/<id>/profile`. Un
 * segmento que no esté en el catálogo de targets simplemente no se estampa y la
 * URL cae en el login normal — degradar a onboarding normal es mejor que la
 * pantalla de error que ven hoy, y de paso mantiene el catálogo como la única
 * lista de aliados válidos (un `?onboarding_target` forjado ya se ignoraba).
 */
export const resolveLegacyAllyRedirect = (url: URL): string => {
	const allySlug = url.pathname
		.split('/')
		.find((segment) => isOnboardingTargetId(`ally:${segment}`));

	const params = new URLSearchParams(url.search);
	if (allySlug) {
		params.set(DEEPLINK_PARAMS.ONBOARDING_TARGET, `ally:${allySlug}`);
	}

	const query = params.toString();
	return query ? `${ROUTES.LOGIN}?${query}` : ROUTES.LOGIN;
};

/**
 * Tiene que ser un loader y no un `<Navigate>` dentro de un componente: una
 * navegación disparada en el primer mount de una ruta lazy durante el boot se
 * descarta en silencio y deja la pantalla en blanco. Este link se abre SIEMPRE
 * en frío (desde WhatsApp o el chat de Zubale), que es justo ese caso.
 */
export const legacyAllyDeeplinkLoader = ({ request }: LoaderFunctionArgs) =>
	redirect(resolveLegacyAllyRedirect(new URL(request.url)));
